import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, Skeleton } from "@mui/material";
import { fetchOrderCountByCategory } from "../../services/orderAnalytics.service";

const COLOR_PALETTE = [
    "#6B78E6",
    "#2EC4B6",
    "#9B5DE5",
    "#7BC96F",
    "#F5A623",
    "#EF476F",
    "#118AB2",
    "#FFD166",
];

const OrderPlacementCategoryWise = ({
    title = "Orders Placement - Category wise",
    dateParams,
    isDateRangeIncomplete,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hoveredKey, setHoveredKey] = useState(null);

    useEffect(() => {
        if (isDateRangeIncomplete) {
            setData([]);
            setIsLoading(false);
            return;
        }
        const { from, to } = dateParams || {};
        if (!from || !to) {
            setData([]);
            setIsLoading(false);
            return;
        }
        let isMounted = true;

        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const list = await fetchOrderCountByCategory({ from, to });

                if (!isMounted) return;

                setHoveredKey(null);

                const totalCount = list.reduce(
                    (sum, item) => sum + (item.orderCount || 0),
                    0
                );

                const transformed = list.map((item, index) => ({
                    key: item.id,
                    value: totalCount
                        ? Math.round((item.orderCount / totalCount) * 100)
                        : 0,
                    color: COLOR_PALETTE[index % COLOR_PALETTE.length],
                }));


                setData(transformed);
            } catch (err) {
                if (isMounted) {
                    console.error(
                        "OrderPlacementCategoryWise: failed to load data",
                        err
                    );
                    setError("Unable to load category data");
                    setData([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };


        loadData();

        return () => {
            isMounted = false;
        };
    }, [dateParams, isDateRangeIncomplete]);

    const SIZE = isMobile ? 120 : 140;
    const CENTER = SIZE / 2;
    const RADIUS = isMobile ? 40 : 50;
    const STROKE = isMobile ? 16 : 18;
    const GAP_ANGLE = 2;
    const SAFE_RADIUS = RADIUS - STROKE / 2;
    let startAngle = 0;

    const polarToCartesian = (cx, cy, r, angle) => {
        const rad = ((angle - 90) * Math.PI) / 180;
        return {
            x: cx + r * Math.cos(rad),
            y: cy + r * Math.sin(rad),
        };
    };

    const describeArc = (cx, cy, r, start, end) => {
        const startPoint = polarToCartesian(cx, cy, r, end);
        const endPoint = polarToCartesian(cx, cy, r, start);
        const largeArcFlag = end - start <= 180 ? 0 : 1;

        return `
            M ${startPoint.x} ${startPoint.y}
            A ${r} ${r} 0 ${largeArcFlag} 0 ${endPoint.x} ${endPoint.y}
        `;
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Skeleton variant="circular" width={100} height={100} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (!data.length) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No category data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: { xs: "auto", sm: 220 },
                p: { xs: 2.5, lg: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: 12, lg: 14 },
                    color: "secondary.main",
                    mb: { xs: 2, sm: 0 },
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    gap: { xs: 4, sm: 4, lg: 2 },
                    mt: 0.5
                }}
            >
                <Box sx={{ width: SIZE, height: SIZE }}>
                    <svg
                        width={SIZE}
                        height={SIZE}
                        viewBox={`-1 -1 ${SIZE + 2} ${SIZE + 2}`}
                    >
                        {data.map((item, i) => {
                            const rawSweep = (item.value / 100) * 360;
                            const sweep = Math.max(rawSweep - GAP_ANGLE, 4);
                            const endAngle = startAngle + sweep;

                            const path = describeArc(
                                CENTER,
                                CENTER,
                                SAFE_RADIUS,
                                startAngle,
                                endAngle
                            );

                            startAngle += sweep + GAP_ANGLE;

                            return (
                                <path
                                    key={i}
                                    d={path}
                                    fill="none"
                                    stroke={item.color}
                                    strokeWidth={STROKE}
                                    strokeOpacity={
                                        hoveredKey && hoveredKey !== item.key
                                            ? 0.3
                                            : 1
                                    }
                                    onMouseEnter={() =>
                                        setHoveredKey(item.key)
                                    }
                                    onMouseLeave={() => setHoveredKey(null)}
                                    style={{
                                        pointerEvents: isLoading || error ? "none" : "auto",
                                        cursor: isLoading || error ? "default" : "pointer",
                                    }}

                                />
                            );
                        })}
                    </svg>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            lg: "1fr",
                            xl: "1fr 1fr",
                        },
                        rowGap: 1,
                        columnGap: 1,
                        flex: 1,
                    }}
                >
                    {data.map((item) => (
                        <Box
                            key={item.key}
                            onMouseEnter={() => setHoveredKey(item.key)}
                            onMouseLeave={() => setHoveredKey(null)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                bgcolor:
                                    hoveredKey === item.key
                                        ? "action.hover"
                                        : "transparent",
                                transition: "all 0.2s ease",
                                pointerEvents: isLoading || error ? "none" : "auto",
                                opacity: isLoading ? 0.5 : 1,
                                cursor: isLoading ? "default" : "pointer",
                            }}
                        >
                            <Box
                                sx={{
                                    width: "2px",
                                    height: "12px",
                                    borderRadius: "1px",
                                    backgroundColor: item.color,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: 9,
                                    color: "text.primary",
                                    fontWeight:
                                        hoveredKey === item.key ? 600 : 400,
                                }}
                            >
                                {item.key} {item.value}%
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(OrderPlacementCategoryWise);